import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { ArrowLeft, ArrowRight, Save, Upload, AlertCircle, CheckCircle } from 'lucide-react';

// Mock data - in a real app, this would come from an API
const mockQuestionnaires = [
  {
    id: '1',
    contractId: '1',
    title: 'Questionnaire Freelance Overeenkomst',
    description: 'Ce questionnaire nous permettra de personnaliser votre contrat de freelance selon vos besoins spécifiques.',
    questions: [
      {
        id: '1-1',
        type: 'text',
        label: 'Nom complet du freelance',
        required: true,
        placeholder: 'Ex: Jean Dupont',
      },
      {
        id: '1-2',
        type: 'text',
        label: 'Adresse du freelance',
        required: true,
        placeholder: 'Adresse complète',
      },
      {
        id: '1-3',
        type: 'textarea',
        label: 'Description détaillée des services à fournir',
        required: true,
        placeholder: 'Décrivez précisément les services que le freelance doit fournir',
        helpText: 'Soyez aussi précis que possible pour éviter tout malentendu ultérieur',
      },
      {
        id: '1-4',
        type: 'date',
        label: 'Date de début de la mission',
        required: true,
      },
      {
        id: '1-5',
        type: 'select',
        label: 'Durée prévue de la mission',
        required: true,
        options: [
          { value: 'less-than-1-month', label: 'Moins d\'un mois' },
          { value: '1-3-months', label: '1 à 3 mois' },
          { value: '3-6-months', label: '3 à 6 mois' },
          { value: '6-12-months', label: '6 à 12 mois' },
          { value: 'more-than-12-months', label: 'Plus de 12 mois' },
        ],
      },
      {
        id: '1-6',
        type: 'radio',
        label: 'Mode de rémunération',
        required: true,
        options: [
          { value: 'hourly', label: 'Taux horaire' },
          { value: 'daily', label: 'Taux journalier' },
          { value: 'fixed', label: 'Montant fixe pour la mission' },
          { value: 'milestone', label: 'Paiement par étapes' },
        ],
      },
      {
        id: '1-7',
        type: 'text',
        label: 'Montant de la rémunération (en €)',
        required: true,
        placeholder: 'Ex: 500',
      },
      {
        id: '1-8',
        type: 'checkbox',
        label: 'Clauses spécifiques à inclure',
        required: false,
        options: [
          { value: 'intellectual-property', label: 'Propriété intellectuelle' },
          { value: 'confidentiality', label: 'Confidentialité renforcée' },
          { value: 'non-compete', label: 'Non-concurrence' },
          { value: 'exclusivity', label: 'Exclusivité' },
        ],
      },
      {
        id: '1-9',
        type: 'file',
        label: 'Documents complémentaires',
        required: false,
        helpText: 'Vous pouvez joindre tout document utile à la préparation du contrat (cahier des charges, etc.)',
      },
    ],
  },
  {
    id: '2',
    contractId: '3',
    title: 'Questionnaire Accord de Confidentialité (NDA)',
    description: 'Ce questionnaire nous permettra de personnaliser votre accord de confidentialité selon vos besoins spécifiques.',
    questions: [
      {
        id: '2-1',
        type: 'text',
        label: 'Nom de la partie divulgatrice',
        required: true,
        placeholder: 'Entité qui partage les informations confidentielles',
      },
      {
        id: '2-2',
        type: 'text',
        label: 'Nom de la partie réceptrice',
        required: true,
        placeholder: 'Entité qui reçoit les informations confidentielles',
      },
      {
        id: '2-3',
        type: 'textarea',
        label: 'Nature des informations confidentielles',
        required: true,
        placeholder: 'Décrivez précisément les types d\'informations qui seront partagées',
      },
      {
        id: '2-4',
        type: 'select',
        label: 'Durée de l\'obligation de confidentialité',
        required: true,
        options: [
          { value: '1-year', label: '1 an' },
          { value: '2-years', label: '2 ans' },
          { value: '3-years', label: '3 ans' },
          { value: '5-years', label: '5 ans' },
          { value: 'indefinite', label: 'Durée indéterminée' },
        ],
      },
    ],
  },
];

// Mock order data
const mockOrders = [
  {
    id: 'ORD-123456',
    contractId: '1',
    status: 'pending',
    createdAt: '2023-06-15',
  },
  {
    id: 'ORD-234567',
    contractId: '3',
    status: 'pending',
    createdAt: '2023-06-14',
  },
];

const Questionnaire: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  
  const [order, setOrder] = useState<any | null>(null);
  const [questionnaire, setQuestionnaire] = useState<any | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps, setTotalSteps] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Create a dynamic schema based on the questionnaire
  const createValidationSchema = (questions: any[]) => {
    const schemaFields: Record<string, any> = {};
    
    questions.forEach(question => {
      if (question.required) {
        switch (question.type) {
          case 'text':
          case 'textarea':
            schemaFields[question.id] = z.string().min(1, 'Ce champ est requis');
            break;
          case 'select':
            schemaFields[question.id] = z.string().min(1, 'Veuillez sélectionner une option');
            break;
          case 'radio':
            schemaFields[question.id] = z.string().min(1, 'Veuillez sélectionner une option');
            break;
          case 'checkbox':
            schemaFields[question.id] = z.array(z.string()).min(1, 'Veuillez sélectionner au moins une option');
            break;
          case 'date':
            schemaFields[question.id] = z.string().min(1, 'Veuillez sélectionner une date');
            break;
          case 'file':
            // File validation is handled separately
            schemaFields[question.id] = z.any().optional();
            break;
          default:
            schemaFields[question.id] = z.string().optional();
        }
      } else {
        if (question.type === 'checkbox') {
          schemaFields[question.id] = z.array(z.string()).optional();
        } else {
          schemaFields[question.id] = z.any().optional();
        }
      }
    });
    
    return z.object(schemaFields);
  };
  
  // Fetch order and questionnaire data
  useEffect(() => {
    if (!orderId) return;
    
    // In a real app, this would be an API call
    const fetchData = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Find the order
        const foundOrder = mockOrders.find(o => o.id === orderId);
        if (!foundOrder) {
          toast.error('Commande non trouvée');
          navigate('/orders');
          return;
        }
        
        setOrder(foundOrder);
        
        // Find the questionnaire for this contract
        const foundQuestionnaire = mockQuestionnaires.find(q => q.contractId === foundOrder.contractId);
        if (!foundQuestionnaire) {
          toast.error('Questionnaire non trouvé');
          navigate('/orders');
          return;
        }
        
        setQuestionnaire(foundQuestionnaire);
        
        // Calculate total steps (group questions into steps of 5)
        const steps = Math.ceil(foundQuestionnaire.questions.length / 5);
        setTotalSteps(steps);
      } catch (error) {
        toast.error('Erreur lors du chargement du questionnaire');
      }
    };
    
    fetchData();
  }, [orderId, navigate]);
  
  // Create form validation schema
  const validationSchema = questionnaire ? createValidationSchema(questionnaire.questions) : z.object({});
  
  // Initialize form
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: questionnaire?.questions.reduce((acc: any, question: any) => {
      if (question.type === 'checkbox') {
        acc[question.id] = [];
      } else {
        acc[question.id] = '';
      }
      return acc;
    }, {}),
  });
  
  // Watch all form values for debugging
  const formValues = watch();
  
  // Get current questions for this step
  const getCurrentQuestions = () => {
    if (!questionnaire) return [];
    
    const startIndex = (currentStep - 1) * 5;
    const endIndex = startIndex + 5;
    
    return questionnaire.questions.slice(startIndex, endIndex);
  };
  
  const onSubmit = async (data: any) => {
    if (currentStep < totalSteps) {
      // Move to next step
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      // Submit the form
      setIsSubmitting(true);
      
      try {
        // In a real app, this would be an API call to save the questionnaire responses
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setIsSubmitted(true);
        toast.success('Questionnaire soumis avec succès');
      } catch (error) {
        toast.error('Erreur lors de la soumission du questionnaire');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  
  const handleSaveDraft = async () => {
    // In a real app, this would save the current state of the form
    toast.success('Brouillon enregistré');
  };
  
  if (!questionnaire || !order) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (isSubmitted) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Questionnaire soumis avec succès</h1>
            
            <p className="text-gray-600 mb-6">
              Merci d'avoir rempli le questionnaire pour votre commande <span className="font-semibold">{orderId}</span>.
              Notre équipe juridique va maintenant analyser vos réponses et préparer votre contrat personnalisé.
            </p>
            
            <div className="bg-blue-50 rounded-md p-6 mb-6 text-left">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Prochaines étapes</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      Vous recevrez une notification par email lorsque votre contrat sera prêt. Vous pourrez également suivre l'avancement de votre commande dans la section "Mes commandes" de votre espace client.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center space-x-4">
              <Link
                to="/orders"
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300"
              >
                Voir mes commandes
              </Link>
              
              <Link
                to="/"
                className="border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium py-2 px-6 rounded-md transition duration-300"
              >
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const currentQuestions = getCurrentQuestions();
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Link to="/orders" className="flex items-center text-blue-600 hover:text-blue-800">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à mes commandes
          </Link>
        </div>
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-800">{questionnaire.title}</h1>
            <p className="text-gray-600 mt-2">{questionnaire.description}</p>
          </div>
          
          <div className="p-6">
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Étape {currentStep} sur {totalSteps}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  {Math.round((currentStep / totalSteps) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)}>
              {currentQuestions.map((question: any) => (
                <div key={question.id} className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {question.label}
                    {question.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  {question.helpText && (
                    <p className="text-sm text-gray-500 mb-2">{question.helpText}</p>
                  )}
                  
                  {question.type === 'text' && (
                    <>
                      <input
                        type="text"
                        className={`block w-full border ${
                          errors[question.id] ? 'border-red-300' : 'border-gray-300'
                        } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                        placeholder={question.placeholder}
                        {...register(question.id)}
                      />
                      {errors[question.id] && (
                        <p className="mt-1 text-sm text-red-600">{(errors[question.id] as any)?.message}</p>
                      )}
                    </>
                  )}
                  
                  {question.type === 'textarea' && (
                    <>
                      <textarea
                        rows={4}
                        className={`block w-full border ${
                          errors[question.id] ? 'border-red-300' : 'border-gray-300'
                        } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                        placeholder={question.placeholder}
                        {...register(question.id)}
                      />
                      {errors[question.id] && (
                        <p className="mt-1 text-sm text-red-600">{(errors[question.id] as any)?.message}</p>
                      )}
                    </>
                  )}
                  
                  {question.type === 'select' && (
                    <>
                      <select
                        className={`block w-full border ${
                          errors[question.id] ? 'border-red-300' : 'border-gray-300'
                        } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                        {...register(question.id)}
                      >
                        <option value="">Sélectionner une option</option>
                        {question.options?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors[question.id] && (
                        <p className="mt-1 text-sm text-red-600">{(errors[question.id] as any)?.message}</p>
                      )}
                    </>
                  )}
                  
                  {question.type === 'radio' && (
                    <>
                      <div className="space-y-2">
                        {question.options?.map((option: any) => (
                          <div key={option.value} className="flex items-center">
                            <input
                              type="radio"
                              id={`${question.id}-${option.value}`}
                              value={option.value}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              {...register(question.id)}
                            />
                            <label
                              htmlFor={`${question.id}-${option.value}`}
                              className="ml-2 block text-sm text-gray-700"
                            >
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      {errors[question.id] && (
                        <p className="mt-1 text-sm text-red-600">{(errors[question.id] as any)?.message}</p>
                      )}
                    </>
                  )}
                  
                  {question.type === 'checkbox' && (
                    <>
                      <div className="space-y-2">
                        <Controller
                          control={control}
                          name={question.id}
                          render={({ field }) => (
                            <>
                              {question.options?.map((option: any) => (
                                <div key={option.value} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`${question.id}-${option.value}`}
                                    value={option.value}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={field.value?.includes(option.value)}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      const value = option.value;
                                      const values = [...(field.value || [])];
                                      
                                      if (checked) {
                                        field.onChange([...values, value]);
                                      } else {
                                        field.onChange(values.filter((v) => v !== value));
                                      }
                                    }}
                                  />
                                  <label
                                    htmlFor={`${question.id}-${option.value}`}
                                    className="ml-2 block text-sm text-gray-700"
                                  >
                                    {option.label}
                                  </label>
                                </div>
                              ))}
                            </>
                          )}
                        />
                      </div>
                      {errors[question.id] && (
                        <p className="mt-1 text-sm text-red-600">{(errors[question.id] as any)?.message}</p>
                      )}
                    </>
                  )}
                  
                  {question.type === 'date' && (
                    <>
                      <input
                        type="date"
                        className={`block w-full border ${
                          errors[question.id] ? 'border-red-300' : 'border-gray-300'
                        } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                        {...register(question.id)}
                      />
                      {errors[question.id] && (
                        <p className="mt-1 text-sm text-red-600">{(errors[question.id] as any)?.message}</p>
                      )}
                    </>
                  )}
                  
                  {question.type === 'file' && (
                    <>
                      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                          <Upload className="mx-auto h-12 w-12 text-gray-400" />
                          <div className="flex text-sm text-gray-600">
                            <label
                              htmlFor={question.id}
                              className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                            >
                              <span>Télécharger un fichier</span>
                              <input
                                id={question.id}
                                type="file"
                                className="sr-only"
                                {...register(question.id)}
                              />
                            </label>
                            <p className="pl-1">ou glisser-déposer</p>
                          </div>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, PDF jusqu'à 10MB
                          </p>
                        </div>
                      </div>
                      {errors[question.id] && (
                        <p className="mt-1 text-sm text-red-600">{(errors[question.id] as any)?.message}</p>
                      )}
                    </>
                  )}
                </div>
              ))}
              
              <div className="border-t border-gray-200 pt-6 flex justify-between">
                <div>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Étape précédente
                    </button>
                  )}
                </div>
                
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    className="flex items-center border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium py-2 px-4 rounded-md transition duration-300"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer le brouillon
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      'Traitement en cours...'
                    ) : currentStep < totalSteps ? (
                      <>
                        Étape suivante
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      'Soumettre'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;